import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Comment } from 'semantic-ui-react';
import { usePopup } from '../../../lib/popup';
import { Markdown } from '../../../lib/custom-ui';

import getDateFormat from '../../../utils/get-date-format';
import CommentEdit from './CommentEdit';
import User from '../../User';
import DeleteStep from '../../DeleteStep';

import styles from './ItemComment.module.scss';

// Parse mentions and return text with mentions replaced by display format
// @[Name](user:123) -> @Name (styled)
const parseMentions = (text) => {
  if (!text) return text;

  const mentionRegex = /@\[([^\]]+)\]\(user:([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  // eslint-disable-next-line no-cond-assign
  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // Add mention placeholder that will be styled by Markdown component
    // We use a format that looks nice in markdown
    parts.push(`**@${match[1]}**`);
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.join('');
};

const ItemComment = React.memo(
  ({ data, createdAt, isPersisted, user, canEdit, onUpdate, onDelete }) => {
    const [t] = useTranslation();

    const commentEdit = useRef(null);

    const handleEditClick = useCallback(() => {
      commentEdit.current.open();
    }, []);

    const DeletePopup = usePopup(DeleteStep);

    // Parse mentions for display
    const displayText = useMemo(() => parseMentions(data.text), [data.text]);

    return (
      <Comment>
        <span className={styles.user}>
          <User name={user.name} avatarUrl={user.avatarUrl} />
        </span>
        <div className={classNames(styles.content)}>
          <CommentEdit
            ref={commentEdit}
            defaultData={data}
            onUpdate={onUpdate}
            text={
              <div className={styles.text}>
                <Markdown linkTarget="_blank">{displayText}</Markdown>
              </div>
            }
            actions={
              <div className={styles.title}>
                <span>
                  <span className={styles.author}>{user.name}</span>
                  <span className={styles.date}>
                    {t(`format:${getDateFormat(createdAt)}`, {
                      postProcess: 'formatDate',
                      value: createdAt,
                    })}
                  </span>
                </span>
                {canEdit && (
                  <Comment.Actions>
                    <Comment.Action
                      as="button"
                      content={t('action.edit')}
                      disabled={!isPersisted}
                      onClick={handleEditClick}
                    />
                    <DeletePopup
                      title="common.deleteComment"
                      content="common.areYouSureYouWantToDeleteThisComment"
                      buttonContent="action.deleteComment"
                      onConfirm={onDelete}
                    >
                      <Comment.Action
                        as="button"
                        content={t('action.delete')}
                        disabled={!isPersisted}
                      />
                    </DeletePopup>
                  </Comment.Actions>
                )}
              </div>
            }
          />
        </div>
      </Comment>
    );
  },
);

ItemComment.propTypes = {
  data: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  createdAt: PropTypes.instanceOf(Date).isRequired,
  isPersisted: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  canEdit: PropTypes.bool.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ItemComment;
